import { Box } from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Column span per widget size. Mobile-first: everything is a single column on
// xs; wider widgets only start spanning two columns once there are at least two.
const spanForSize = size => {
  switch (size) {
    case 'large':
      return { xs: 'span 1', sm: 'span 2', lg: 'span 2' };
    case 'wide':
      return { xs: 'span 1', sm: 'span 2', md: 'span 2', lg: 'span 2', xl: 'span 2' };
    default:
      return { xs: 'span 1' };
  }
};

function SortableCell({ id, size, editMode, render }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !editMode,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.85 : 1,
  };

  const dragHandleProps = editMode ? { ...attributes, ...listeners } : undefined;

  return (
    <Box ref={setNodeRef} style={style} sx={{ gridColumn: spanForSize(size), minWidth: 0 }}>
      {render({ dragHandleProps, isDragging })}
    </Box>
  );
}

// Responsive CSS-grid of sortable widget cells. Reordering is only active in
// edit mode; otherwise the cards behave as plain, navigable tiles.
export default function DashboardGrid({ items, editMode, onReorder, renderItem }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const ids = items.map(item => item.id);

  const handleDragEnd = event => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = [...ids];
    next.splice(oldIndex, 1);
    next.splice(newIndex, 0, active.id);
    onReorder(next);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 1.5, sm: 2 },
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
              xl: 'repeat(4, minmax(0, 1fr))',
            },
            alignItems: 'stretch',
          }}
        >
          {items.map(item => (
            <SortableCell
              key={item.id}
              id={item.id}
              size={item.size}
              editMode={editMode}
              render={renderItem.bind(null, item)}
            />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
}
